import {
  Alert,
  AnimatedContentLoader,
  DoubleModal,
  HeaderBlock,
  PaymentButton,
  Prompt
} from '@components'
import { AttachmentAnalysisStatus, PurchaseInvoiceImportSource } from '@constants'
import {
  FeatureWrapper,
  PermissionSet,
  useBusinessContext,
  useFeature,
  usePermission
} from '@context'
import { DocumentAttachmentPreview } from '@containers/DocumentAttachmentPreview'
import { DocumentFormModal } from '@pages/DocumentPage/DocumentForm'
import { deletePurchaseInvoice, invalidateDashboardQueries, IDocument } from '@query'
import { defaultValuesForPurchaseInvoiceDocument, formatDate, getApiErrorMessage } from '@utils'
import { createPurchaseInvoicePayment, IPaymentDetails, updatePurchaseInvoice } from '@query'
import { usePurchaseInvoice } from '@root/query/hooks/invoices'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaCheckCircle, FaFeatherAlt, FaMinusSquare, FaPen, FaTrash } from 'react-icons/fa'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DueDateStatusIcon } from './combinedStatus'
import { PurchaseInvoiceStatusBadge } from './InvoiceStatusBadge'
import {
  AttachmentSection,
  ContentFooter,
  DetailMetaGrid,
  DetailSection,
  ScrollableContent,
  SectionLabel,
  StatusBadgesRow,
  StyledProductDetails
} from './InvoicingDetails.styled'
import { PurchaseInvoicePaymentInfo } from './PurchaseInvoicePaymentInfo'
import { AttachmentSelector } from '@containers/AttachmentSelector'
import PurchaseInvoiceForm from './PurchaseInvoiceForm'
import { HolviPaymentFormModal } from '@containers/PaymentFormModal'
import { HolviPaymentStatusTracker } from '@containers/PaymentFormModal/HolviPaymentStatusTracker.tsx'
import { isPaymentInProgress, isPaymentPending } from '@utils/pain.ts'
import { InvoiceDocumentLinkField, InvoicePaidAtField } from './InvoiceDetailSharedFields'
import toast from 'react-hot-toast'
import { PISPaymentStatusBadge } from './PISPaymentStatusBadge'
import { usePISPurchaseInvoicePayment } from './hooks/usePISPurchaseInvoicePayment'
import { TagDisplay } from '@components/TagDisplay/TagDisplay'
import { InvoiceDetailField } from './InvoiceDetailField'
import { InvoiceExtractingCard } from './InvoiceExtractingCard'
import { PurchaseInvoiceExpenseClaimSection } from './PurchaseInvoiceExpenseClaimSection'

interface Props {
  invoiceId: number
  onClose: () => void
}

export const PurchaseInvoiceDetails: React.FC<Props> = ({
  invoiceId: purchaseInvoiceId,
  onClose
}) => {
  const [t] = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const queryClient = useQueryClient()
  const [showDocumentForm, setShowDocumentForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const hasBookkeepingEditPermission = usePermission(PermissionSet.BOOKKEEPING_EDIT)
  const hasBookkeepingViewPermission = usePermission(PermissionSet.BOOKKEEPING_VIEW)
  const hasEditPermission = usePermission(PermissionSet.INVOICING_EDIT)
  const canManageIntegrations = usePermission(PermissionSet.INTEGRATION_ADMIN)
  const { enabled: holviPaymentsEnabled } = useFeature('payment_initiation')
  const { enabled: holviIntegrationEnabled } = useFeature('holvi_integration')

  const { data, isLoading } = usePurchaseInvoice({ purchaseInvoiceId })

  const defaultValuesForDocumentForm = useMemo<Partial<IDocument>>(() => {
    return defaultValuesForPurchaseInvoiceDocument(data, business?.account_payables)
  }, [business?.account_payables, data])

  const isImportedFromAnalyzedSource =
    data?.import_source === PurchaseInvoiceImportSource.EMAIL ||
    data?.import_source === PurchaseInvoiceImportSource.UPLOAD

  const isEmailPurchaseInvoiceDataExtractionInProgress =
    (data?.original_attachment?.analysis_status === AttachmentAnalysisStatus.IN_PROGRESS ||
      data?.original_attachment?.analysis_status === AttachmentAnalysisStatus.PENDING) &&
    isImportedFromAnalyzedSource

  const markAsPaid = useMutation<unknown, unknown, boolean>({
    mutationFn: isPaid =>
      updatePurchaseInvoice(
        { slug, purchaseInvoiceId },
        {
          is_paid: isPaid
        }
      ),
    ...{
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [slug, 'purchase_invoices'] }),
          queryClient.invalidateQueries({ queryKey: [slug, 'combined_invoices'] }),
          invalidateDashboardQueries(queryClient, slug)
        ])
      }
    }
  })

  const deleteMutation = useMutation({
    mutationFn: () => deletePurchaseInvoice({ slug, purchaseInvoiceId }),
    ...{
      onSuccess: async () => {
        onClose()
        setShowDelete(false)
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [slug, 'purchase_invoices'] }),
          queryClient.invalidateQueries({ queryKey: [slug, 'combined_invoices'] }),
          invalidateDashboardQueries(queryClient, slug)
        ])
        toast.success(t('invoicing.purchaseInvoiceDeleteForm.success'))
      },
      onError: error => {
        const status =
          (error as { response?: { status?: number }; status?: number })?.response?.status ??
          (error as { status?: number })?.status
        if (status === 423) {
          return
        }
        toast.error(t('invoicing.purchaseInvoiceDeleteForm.error'))
      }
    }
  })

  const isPaymentBusy = Boolean(
    data?.payment && (isPaymentInProgress(data.payment) || isPaymentPending(data.payment))
  )

  const isExpenseClaim = data?.import_source === PurchaseInvoiceImportSource.EXPENSE_CLAIM

  const allowPurchaseInvoiceEdit =
    !isExpenseClaim &&
    !isEmailPurchaseInvoiceDataExtractionInProgress &&
    !data?.is_paid &&
    !isPaymentBusy &&
    hasEditPermission

  const showImportNotice =
    isImportedFromAnalyzedSource &&
    !isEmailPurchaseInvoiceDataExtractionInProgress &&
    !data?.is_paid

  const menuItems = useMemo(() => {
    const items = [
      ...(hasBookkeepingEditPermission && !isExpenseClaim
        ? [
            {
              Icon: FaFeatherAlt,
              name: t('invoicing.details.createDocument'),
              intent: 'default',
              group: 'actions',
              onClick: () => setShowDocumentForm(true)
            }
          ]
        : []),

      ...(allowPurchaseInvoiceEdit
        ? [
            {
              Icon: FaPen,
              name: t('general.edit'),
              intent: 'default',
              group: 'actions',
              onClick: () => setShowEditForm(true)
            }
          ]
        : []),

      ...(hasEditPermission && !isEmailPurchaseInvoiceDataExtractionInProgress
        ? [
            ...(!data?.is_paid
              ? [
                  {
                    Icon: FaCheckCircle,
                    name: t('invoicing.purchaseInvoiceDetails.markAsPaid'),
                    intent: 'default',
                    group: 'actions',
                    onClick: () => markAsPaid.mutateAsync(true)
                  }
                ]
              : [
                  {
                    Icon: FaMinusSquare,
                    name: t('invoicing.purchaseInvoiceDetails.markAsUnpaid'),
                    intent: 'default',
                    group: 'actions',
                    onClick: () => markAsPaid.mutateAsync(false)
                  }
                ])
          ]
        : []),

      ...(hasEditPermission && !isExpenseClaim
        ? [
            {
              name: t('general.delete'),
              Icon: FaTrash,
              onClick: () => setShowDelete(true),
              intent: 'danger',
              group: 'danger'
            }
          ]
        : [])
    ]
    return items
  }, [
    data?.is_paid,
    allowPurchaseInvoiceEdit,
    hasBookkeepingEditPermission,
    hasEditPermission,
    isEmailPurchaseInvoiceDataExtractionInProgress,
    isExpenseClaim
  ])

  const isDeletable = data?.is_deletable !== false

  const isPaymentBlocked = data?.payment ? isPaymentInProgress(data.payment) : false

  const { createPayment: createPISPayment, isPending: isPISPaymentPending } =
    usePISPurchaseInvoicePayment({
      slug,
      purchaseInvoiceId,
      isReady: Boolean(data),
      fallbackErrorMessage: t('general.error')
    })

  const handlePISPaymentClick = async (options?: {
    shortcutId?: number
    providerCode?: string
  }): Promise<boolean> => {
    if (!data) {
      return false
    }
    try {
      await createPISPayment(options)
      return true
    } catch (error) {
      const message = getApiErrorMessage(error, t('general.error'))
      toast.error(message)
      return false
    }
  }

  return (
    <>
      <StyledProductDetails>
        <HeaderBlock
          header={t('invoicing.purchaseInvoiceDetails.title')}
          menuItems={menuItems}
          withDivider
        />

        <ScrollableContent>
          <AnimatedContentLoader isLoading={isLoading}>
            {data && (
              <>
                <DetailSection>
                  <StatusBadgesRow>
                    <PurchaseInvoiceStatusBadge isPaid={data.is_paid} size="md" />
                    {data?.pis_payment?.statuses?.length ? (
                      <PISPaymentStatusBadge statuses={data?.pis_payment?.statuses} />
                    ) : null}
                  </StatusBadgesRow>
                </DetailSection>

                <FeatureWrapper feature="payment_initiation">
                  <HolviPaymentStatusTracker paymentId={data?.payment?.id} />
                </FeatureWrapper>

                {isEmailPurchaseInvoiceDataExtractionInProgress && (
                  <DetailSection>
                    <InvoiceExtractingCard />
                  </DetailSection>
                )}

                {!isEmailPurchaseInvoiceDataExtractionInProgress && (
                  <DetailSection>
                    <DetailMetaGrid>
                      {data?.sender_name && (
                        <InvoiceDetailField label={t('invoicing.purchaseInvoiceDetails.sender')}>
                          {data.sender_name}
                        </InvoiceDetailField>
                      )}

                      {data?.invoicing_date && (
                        <InvoiceDetailField
                          label={t('invoicing.purchaseInvoiceDetails.invoicingDate')}
                        >
                          {formatDate(data.invoicing_date)}
                        </InvoiceDetailField>
                      )}

                      {data?.delivery_date && (
                        <InvoiceDetailField
                          label={t('invoicing.purchaseInvoiceDetails.deliveryDate')}
                        >
                          {formatDate(data.delivery_date)}
                        </InvoiceDetailField>
                      )}

                      {data?.due_date && (
                        <InvoiceDetailField
                          label={t('invoicing.purchaseInvoiceDetails.dueDate')}
                          valueContainer
                          emphasize={data.is_past_due && !data.is_paid ? 'warning' : undefined}
                        >
                          {formatDate(data.due_date)}
                          {data.is_past_due && !data.is_paid && <DueDateStatusIcon />}
                        </InvoiceDetailField>
                      )}

                      <InvoicePaidAtField
                        label={t('invoicing.purchaseInvoiceDetails.paidAt')}
                        isPaid={data.is_paid}
                        bankTransaction={data?.bank_transaction}
                        paymentDate={data?.payment_date}
                      />

                      {hasBookkeepingViewPermission && (
                        <InvoiceDocumentLinkField
                          slug={slug}
                          document={data?.document}
                          label={t('invoicing.purchaseInvoiceDetails.document')}
                          moveToDocumentLabel={t('invoicing.purchaseInvoiceDetails.moveToDocument')}
                          moveToDraftLabel={t('invoicing.purchaseInvoiceDetails.moveToDraft')}
                        />
                      )}
                    </DetailMetaGrid>
                  </DetailSection>
                )}

                {isExpenseClaim && data.expense_claim && (
                  <PurchaseInvoiceExpenseClaimSection
                    slug={slug}
                    invoice={data}
                    expenseClaim={data.expense_claim}
                    showDocumentLink={hasBookkeepingViewPermission}
                  />
                )}

                {!isEmailPurchaseInvoiceDataExtractionInProgress && data?.tag_ids?.length > 0 && (
                  <DetailSection>
                    <TagDisplay
                      tagIds={data.tag_ids}
                      label={<SectionLabel>{t('document.tags')}</SectionLabel>}
                    />
                  </DetailSection>
                )}

                {!isExpenseClaim && data?.original_attachment?.id && (
                  <DetailSection>
                    <SectionLabel>{t('invoicing.purchaseInvoiceDetails.preview')}</SectionLabel>
                    <AttachmentSection>
                      <AttachmentSelector value={[data.original_attachment.id]} disabled={true} />
                    </AttachmentSection>
                  </DetailSection>
                )}

                {!isExpenseClaim &&
                  !isEmailPurchaseInvoiceDataExtractionInProgress &&
                  data?.attachments?.length > 0 && (
                    <DetailSection>
                      <SectionLabel>
                        {t('invoicing.purchaseInvoiceDetails.attachments')}
                      </SectionLabel>
                      <AttachmentSection>
                        <AttachmentSelector value={data.attachments} disabled={true} />
                      </AttachmentSection>
                    </DetailSection>
                  )}

                {!isEmailPurchaseInvoiceDataExtractionInProgress && (
                  <DetailSection>
                    <PurchaseInvoicePaymentInfo
                      invoice={data}
                      showImportNotice={showImportNotice}
                    />
                  </DetailSection>
                )}
              </>
            )}
          </AnimatedContentLoader>
        </ScrollableContent>
        {!isLoading && data && !data.is_paid && !isEmailPurchaseInvoiceDataExtractionInProgress && (
          <ContentFooter>
            <PaymentButton
              amount={data.amount ?? 0}
              currency={
                data.currency ? (data.currency.trim() === '' ? 'EUR' : data.currency) : 'EUR'
              }
              showAmount={false}
              disabled={isPaymentBlocked || isPISPaymentPending}
              type="button"
              businessSlug={slug}
              holviEnabled={
                holviPaymentsEnabled && holviIntegrationEnabled && canManageIntegrations
              }
              holviConnectionEnabled={business?.holvi_nocfo_connection_enabled}
              onHolviClick={() => setShowPaymentForm(true)}
              onPISProviderSelect={providerCode =>
                handlePISPaymentClick({
                  providerCode
                })
              }
              onPISShortcutClick={shortcut =>
                handlePISPaymentClick({
                  shortcutId: shortcut.id
                })
              }
            />
          </ContentFooter>
        )}
      </StyledProductDetails>

      <DocumentFormModal
        isVisible={showDocumentForm}
        onClose={() => setShowDocumentForm(false)}
        defaults={defaultValuesForDocumentForm}
        contactSuggestion={
          business?.has_accrual_based_entries_for_invoicing ? data?.sender_name : undefined
        }
      />

      <DoubleModal
        isVisible={showEditForm}
        oneModalWidthMin={480}
        oneModalWidthMax={700}
        height={900}
        mainHeader={t('invoicing.purchaseInvoiceForm.title')}
        onClose={() => setShowEditForm(false)}
        mainContent={
          <PurchaseInvoiceForm purchaseInvoice={data} onClose={() => setShowEditForm(false)} />
        }
        secondaryContent={
          <DocumentAttachmentPreview
            attachmentIds={data?.original_attachment?.id ? [data.original_attachment.id] : []}
          />
        }
      />

      {showPaymentForm && data?.sender_name && (
        <HolviPaymentFormModal
          isVisible={showPaymentForm}
          onClose={() => setShowPaymentForm(false)}
          defaults={{
            creditor: {
              name: data?.sender_name,
              iban: data?.sender_bank_account,
              bic: data?.sender_bank_bic
            },
            amount: {
              amount: data?.amount,
              currency: data?.currency || 'EUR'
            },
            requested_execution_date: data?.due_date,
            reference: data?.reference,
            message: data?.message
          }}
          createPaymentFn={async (payment: IPaymentDetails) => {
            const result = await createPurchaseInvoicePayment(
              { slug, purchaseInvoiceId: data.id },
              payment
            )
            return result
          }}
        />
      )}

      <Prompt
        isVisible={showDelete}
        onClose={() => setShowDelete(false)}
        title={t('invoicing.purchaseInvoiceDeleteForm.title')}
        description={
          <>
            <div>{t('invoicing.purchaseInvoiceDeleteForm.description')}</div>
            <Alert
              type="error"
              title={t('invoicing.purchaseInvoiceDeleteForm.cannotDelete')}
              isVisible={!isDeletable}
              description={t('invoicing.purchaseInvoiceDeleteForm.cannotDeleteDescription')}
            />
          </>
        }
        buttons={[
          {
            text: t('general.cancel'),
            intent: 'default',
            action: () => setShowDelete(false),
            disabled: deleteMutation.isPending
          },
          {
            text: t('general.delete'),
            intent: 'danger',
            action: async () => {
              try {
                await deleteMutation.mutateAsync()
              } catch {
                return
              }
            },
            showSpinner: deleteMutation.isPending,
            disabled: !isDeletable || deleteMutation.isPending
          }
        ]}
      />
    </>
  )
}
